<template>
  <div
    v-if="props.myLimitedExpressTicket.ticketing_status == '01'"
    class="my-ticket-foot-button-block"
  >
    <ItemButton
      style="margin-top: 10px; margin-bottom: 40px"
      action-type="btn-submit"
      label="購入する"
      @clicked="goToPurchasePage"
    />
    <ItemButton
      action-type="btn-submit"
      label="予約変更"
      @clicked="goToChangePage"
    />
    <ItemButton
      action-type="btn-submit"
      label="予約取り消し"
      @clicked="openDialogConfirmCancelReservation"
    />
  </div>
  <div
    v-else-if="props.myLimitedExpressTicket.ticketing_status == '02'"
    class="my-ticket-foot-button-block"
  >
    <ItemButton
      action-type="btn-submit"
      label="チケット内容変更"
      @clicked="goToChangePage"
    />
    <ItemButton
      v-if="isUseCoupon && isRefundable"
      action-type="btn-submit"
      label="チケット取り消し"
      @clicked="openDialogConfirmCancelMyTicket"
    />
    <ItemButton
      v-else-if="isRefundable || isUsedAlipay"
      action-type="btn-submit"
      :is-disabled="isDisabled"
      label="払いもどし"
      @clicked="goToRefundPage"
    />

    <!-- 引換券使用時は共有なし -->
    <ItemButton
      v-if="!isUseCoupon"
      action-type="btn-submit"
      label="共有"
      @clicked="shareTicket"
    />
  </div>

  <!-- 予約自動取り消し後、変更・取消不可のダイアログ -->
  <DialogCanNotChangeAfterCanceled ref="DialogCanNotChangeAfterCanceled" />

  <!-- 予約取り消しの確認ダイアログ -->
  <DialogConfirmCancelReservation
    ref="DialogConfirmCancelReservation"
    @cancelReservation="cancelReservation"
  />

  <!-- 予約取り消し完了ダイアログ -->
  <DialogCancelCompleted
    ref="DialogCancelCompleted"
    :close-button-clicked="backToMfittIndexPage"
  />

  <!-- チケット（特付・サブスク）取り消しの確認ダイアログ -->
  <DialogConfirmCancelMyTicket
    ref="DialogConfirmCancelMyTicket"
    @cancelMyTicket="cancelMyTicket"
  />

  <!-- チケット(特付・サブスク)取り消し完了ダイアログ -->
  <DialogCouponCancelCompleted
    ref="DialogCouponCancelCompleted"
    :close-button-clicked="backToMfittIndexPage"
  />

  <!-- 発車1分経過後、チケット(特付・サブスク)取り消し不可のダイアログ -->
  <DialogCanNotCancelAfterDeparture ref="DialogCanNotCancelAfterDeparture" />

  <!-- 電波状況が悪い場合のダイアログ -->
  <DialogBadReception ref="DialogBadReception" />

  <DialogDuringMaintenance ref="DialogDuringMaintenance" />

  <DialogUnexpectedError ref="DialogUnexpectedError" />

  <!-- ローディングダイアログ -->
  <DialogLoading ref="DialogLoading" :timeout-seconds="21000" />
</template>
<script>
import { computed, ref } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";

import API from "@/API";

import mfittService from "@/services/MFITT";
import nativeHandlerService from "@/services/nativeHandler";

import commonMethodsMixin from "@/mixins/Common/CommonMethodsMixin";

export default {
  name: "MFITTMyTicketActions",
  props: {
    myLimitedExpressTicket: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();

    const DialogCanNotChangeAfterCanceled = ref(null);
    const DialogConfirmCancelReservation = ref(null);
    const DialogConfirmCancelMyTicket = ref(null);
    const DialogCanNotCancelAfterDeparture = ref(null);
    const DialogCouponCancelCompleted = ref(null);
    const DialogCancelCompleted = ref(null);
    const DialogBadReception = ref(null);
    const DialogUnexpectedError = ref(null);
    const DialogDuringMaintenance = ref(null);
    const DialogLoading = ref(null);

    const selectedCoupon = store.getters["MFITT/selectedCoupon"];
    const isUseCoupon = selectedCoupon != null;
    const isRefundable = computed(() => {
      return (
        props.myLimitedExpressTicket.refundable == null ||
        props.myLimitedExpressTicket.refundable == true
      );
    });

    const isUsedAlipay = mfittService.isAlipayCommodityCode(
      props.myLimitedExpressTicket.commodity_code
    );

    const isDisabled = computed(() => {
      // Check enable/disable refurnable button
      return !(isUsedAlipay || props.myLimitedExpressTicket.refundable);
    });

    const cancelReservation = async () => {
      DialogConfirmCancelReservation.value.closeDialog();
      DialogLoading.value.openDialog();
      try {
        await API.putTicketingStatus(route.params.id, "cancel");
        DialogLoading.value.closeDialog();
        DialogCancelCompleted.value.openDialog();
      } catch (e) {
        // 電波がない場合、e.response.data.messageが存在しない
        // loadingを表示し続け、そちらでタイムアウトさせる
        if (e.response.data.message) {
          DialogLoading.value.closeDialog();
          // 予約が自動取り消し済の場合
          if (
            e.response.data.message ===
            "ticketing_status指定された特急券は予約取消済みです"
          ) {
            DialogCanNotChangeAfterCanceled.value.openDialog();
          } else if (e.response.data.message === "回線が込み合っています") {
            DialogBadReception.value.openDialog();
            // MWのメンテナンス中
          } else if (e.response.status == 503) {
            DialogDuringMaintenance.value.openDialog(e.response.data);
          } else {
            // nativeにエラー内容送信
            nativeHandlerService.ncSendMfittError(e.response.data.message);
            DialogUnexpectedError.value.openDialog();
          }
        }
      }
    };

    const cancelMyTicket = async () => {
      DialogConfirmCancelMyTicket.value.closeDialog();
      DialogLoading.value.openDialog();
      try {
        await API.postRefundTicket(props.myLimitedExpressTicket.id, {
          cancel_reason: "refund",
          is_partial_refund: false,
        });
        DialogLoading.value.closeDialog();
        DialogCouponCancelCompleted.value.openDialog();
      } catch (e) {
        // 電波がない場合、e.response.data.messageが存在しない
        // loadingを表示し続け、そちらでタイムアウトさせる
        if (e.response.data.message) {
          DialogLoading.value.closeDialog();
          console.log("Error in postRefundTicket", e);
          // 払い戻し期限を過ぎている場合
          if (
            e.response.data.message ===
            "指定された特急券の列車は既に発車済みの為、払戻できません"
          ) {
            DialogCanNotCancelAfterDeparture.value.openDialog();
            // チケット情報更新のエラー
          } else if (e.response.data.message === "回線が込み合っています") {
            DialogBadReception.value.openDialog();
            // MWのメンテナンス中
          } else if (e.response.status == 503) {
            DialogDuringMaintenance.value.openDialog();
          } else {
            // nativeにエラー内容送信
            nativeHandlerService.ncSendMfittError(e.response.data.message);
            DialogUnexpectedError.value.openDialog();
          }
        } else {
          nativeHandlerService.ncSendMfittError(
            "MFITTMyTicketActions cancelMyTicket()"
          );
        }
      }
    };

    const getTicketStatusForChangePageQuery = () => {
      if (props.myLimitedExpressTicket.ticketing_status == "01") {
        return "reserved";
      } else if (props.myLimitedExpressTicket.ticketing_status == "02") {
        return "purchased";
      } else {
        throw "invalid ticket status for change";
      }
    };

    const goToChangePage = async () => {
      if (
        props.myLimitedExpressTicket.ticketing_status == "01" &&
        mfittService.isOverAutoCanceledAt(
          props.myLimitedExpressTicket.auto_canceled_at
        )
      ) {
        // 購入前で、購入期限を過ぎていたらダイアログ表示
        DialogCanNotChangeAfterCanceled.value.openDialog();
      } else {
        router.push({
          name: "MfittMyTicketChange",
          params: { id: route.params.id },
          query: isUseCoupon
            ? {
                ticketStatus: getTicketStatusForChangePageQuery(),
                coupon_id: selectedCoupon.id,
              }
            : { ticketStatus: getTicketStatusForChangePageQuery() },
        });
      }
    };

    const goToPurchasePage = async () => {
      router.push({
        name: "MfittSelectPaymentMethod",
        params: { id: route.params.id },
        query: {
          payment_limited_express_ticket_id: route.params.id,
        },
      });
    };

    const goToRefundPage = async () => {
      router.push({
        name: "MfittMyTicketRefund",
        params: { id: route.params.id },
      });
    };

    const shareTicket = () => {
      // native用のjson
      const json = JSON.stringify({
        title: "EMotロマンスカーチケット内容",
        boarding_date: {
          label: "乗車日",
          value: props.myLimitedExpressTicket.boarding_date.replace(/-/g, ""),
        },
        train_name: {
          label: "列車名",
          value: `${props.myLimitedExpressTicket.train.name}(${props.myLimitedExpressTicket.train.cars_formation.name})`,
        },
        time: "時間",
        departure_at: props.myLimitedExpressTicket.departure_at,
        arrival_at: props.myLimitedExpressTicket.arrival_at,
        from_station_name: props.myLimitedExpressTicket.from_station.name,
        to_station_name: props.myLimitedExpressTicket.to_station.name,
        count: "人数",
        adult_count: {
          label: "おとな",
          value: props.myLimitedExpressTicket.adult_count,
        },
        child_count: {
          label: "こども",
          value: props.myLimitedExpressTicket.child_count,
        },
        seat_attribute: {
          label: "座席種類",
          value:
            commonMethodsMixin.methods.displaySeatExclusiveCar(
              props.myLimitedExpressTicket.train.cars_formation.cars,
              props.myLimitedExpressTicket.seats
            ) ||
            commonMethodsMixin.methods.displayMfittSeatAttribute(
              props.myLimitedExpressTicket.seats[0].seat_attribute
            ),
        },
        seat: "座席",
        seats: convertSeatsForShare(props.myLimitedExpressTicket.seats),
        limited_express_ticket_id: {
          label: "確認番号",
          value: props.myLimitedExpressTicket.auth_code,
        },
        description: [
          {
            label:
              "※このメールは、受信者が特急券・特急整理票を保有していることの証明にはなりません",
          },
          {
            label:
              "※購入者が変更、払い戻しをされた場合、EMotサービスを退会された場合には、以上の内容は無効になります",
          },
        ],
      });

      nativeHandlerService.ncOpenShare(json);
    };

    const convertSeatsForShare = (seats) => {
      return seats.map((seat) => {
        return {
          car_number: `${seat.car_number}号車`,
          display_number: seat.display_number,
        };
      });
    };

    const openDialogConfirmCancelMyTicket = async () => {
      DialogConfirmCancelMyTicket.value.openDialog();
    };

    const openDialogConfirmCancelReservation = async () => {
      DialogConfirmCancelReservation.value.openDialog();
    };

    const backToMfittIndexPage = () => {
      nativeHandlerService.returnNativeAppMfittIndexPage();
    };

    return {
      cancelReservation,
      cancelMyTicket,
      goToChangePage,
      goToPurchasePage,
      goToRefundPage,
      shareTicket,
      DialogCanNotChangeAfterCanceled,
      DialogConfirmCancelReservation,
      DialogConfirmCancelMyTicket,
      DialogCanNotCancelAfterDeparture,
      DialogCouponCancelCompleted,
      DialogCancelCompleted,
      DialogBadReception,
      DialogUnexpectedError,
      DialogDuringMaintenance,
      DialogLoading,
      isUseCoupon,
      isRefundable,
      isDisabled,
      isUsedAlipay,
      openDialogConfirmCancelMyTicket,
      openDialogConfirmCancelReservation,
      backToMfittIndexPage,
      props,
    };
  },
};
</script>
<style>
.my-ticket-foot-button-block {
  position: fixed;
  z-index: 99;
  left: 0;
  width: 100%;
  bottom: 0;
  background-color: #ffb206;
  padding: 15px 15px 20px 15px;
}

.my-ticket-foot-button-block button {
  width: calc(100% - 30px);
  height: 46px;
  border: none;
  border-radius: 30px;
  background-color: #ffffff;
  padding: 10px;
  font-size: 14px;
  font-weight: bold;
  color: #000000;
  box-shadow: var(--defaultBoxShadow);
}
</style>
